import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function CategoryDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (category: typeof categories[0]) => {
    setSelected(category.name);
    setIsOpen(false);
    window.open(category.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="py-16 md:py-20 bg-background-100">
      <div className="max-w-[700px] mx-auto px-4 md:px-6">
        <div className="text-center mb-6">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold tracking-wide uppercase mb-3">
            Quick Navigation
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            Jump to Any Category
          </h2>
          <p className="mt-2 text-sm md:text-base text-foreground-600">
            Select a category below and we&apos;ll take you straight to the best finds on kaspreadsheet.com
          </p>
        </div>

        <div ref={dropdownRef} className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center justify-between px-5 py-4 rounded-2xl bg-background-50 border border-background-200 hover:border-primary-300 transition-all cursor-pointer group"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100">
                <i className="ri-grid-line text-primary-600"></i>
              </div>
              <span className="text-base font-medium text-foreground-800">
                {selected || 'Choose a category...'}
              </span>
            </div>
            <div className="w-8 h-8 flex items-center justify-center">
              <i
                className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform duration-200 ${
                  isOpen ? 'rotate-180' : ''
                }`}
              ></i>
            </div>
          </button>

          {isOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 rounded-2xl border border-background-200 shadow-lg overflow-hidden z-40 max-h-[420px] overflow-y-auto">
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => handleSelect(cat)}
                  className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-primary-50 transition-colors cursor-pointer text-left border-b border-background-100 last:border-b-0"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100">
                    <i className={`${cat.icon} text-secondary-700 text-sm`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-800 flex-1">
                    {cat.name}
                  </span>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-xs text-foreground-400"></i>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}